import { ReactNode } from 'react';
import { ReplaySubject, Subject } from 'rxjs';
import { NotificationOptions, NotificationSeverity } from './types';

/**
 * Create a new notification stream
 */
export const createNotification$ = () => new ReplaySubject<NotificationOptions>();

/**
 * Queue a notification of the specified severity
 *
 * @param {Subject} notification$ Target notification observable
 * @param {string} caption Notification caption
 * @param {string} message Notification message
 * @param {Severity} severity Target severity
 * @param {number} duration Notification duration in milliseconds
 */
const queueNotification = (
  notification$: Subject<NotificationOptions>,
  caption: string,
  message: ReactNode,
  severity: NotificationSeverity,
  duration?: number,
) =>
  notification$.next({
    caption,
    duration,
    message,
    severity,
  });

/**
 * Queue an error notification
 *
 * @param {Subject} notification$ Target notification observable
 * @param {string} caption Notification caption
 * @param {string} message Notification message
 * @param {number} duration Notification duration in milliseconds
 */
export const queueErrorNotification = (
  notification$: Subject<NotificationOptions>,
  caption: string,
  message: ReactNode,
  duration?: number,
) => queueNotification(notification$, caption, message, 'error', duration);

/**
 * Queue an info notification
 *
 * @param {Subject} notification$ Target notification observable
 * @param {string} caption Notification caption
 * @param {string} message Notification message
 * @param {number} duration Notification duration in milliseconds
 */
export const queueInfoNotification = (
  notification$: Subject<NotificationOptions>,
  caption: string,
  message: ReactNode,
  duration?: number,
) => queueNotification(notification$, caption, message, 'info', duration);

/**
 * Queue a success notification
 *
 * @param {Subject} notification$ Target notification observable
 * @param {string} caption Notification caption
 * @param {string} message Notification message
 * @param {number} duration Notification duration in milliseconds
 */
export const queueSuccessNotification = (
  notification$: Subject<NotificationOptions>,
  caption: string,
  message: ReactNode,
  duration?: number,
) => queueNotification(notification$, caption, message, 'success', duration);

/**
 * Queue a warning notification
 *
 * @param {Subject} notification$ Target notification observable
 * @param {string} caption Notification caption
 * @param {string} message Notification message
 * @param {number} duration Notification duration in milliseconds
 */
export const queueWarningNotification = (
  notification$: Subject<NotificationOptions>,
  caption: string,
  message: ReactNode,
  duration?: number,
) => queueNotification(notification$, caption, message, 'warning', duration);
